import { Component, createElement } from 'react';
import find from 'lodash/find';
import flatMap from 'lodash/flatMap';
import identity from 'lodash/identity';
import isPlainObject from 'lodash/isPlainObject';
import omit from 'lodash/omit';
import uniq from 'lodash/uniq';
import uniqueId from 'lodash/uniqueId';
import zip from 'lodash/zip';
import flow from 'lodash/flow';
import assert from 'assert';
import hoistStatics from 'hoist-non-react-statics';
import { Observable } from 'rxjs';

import {
  PENDING,
  expand,
  watchAll,
  isError,
} from '../../../muster-utils';
import {
  FRAGMENTS_SYMBOL,
  serialize,
  initPathValues,
  addPathValues,
  formatPathValues,
  formatTime,
  pendingPaths,
  filterPathValues,
  replacePath,
  deserializeMusterError,
  consoleGroupCollapsed,
  consoleGroupEnd,
} from '../utils';
import RouterShape from '../utils/router-shape';
import getQueryPaths from './get-query-paths';
import createQueryState from './create-query-state';

// using the .native extension lookup in react native is the only reliable way
// of ensuring that we are in the correct environment
import renderError from './render-error';

const PRODUCTION = process.env.NODE_ENV === 'production';

function getDisplayName(WrappedComponent) {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}

function validateOptions(options, componentName) {
  assert(
    isPlainObject(options),
    (
      'Unexpected options type passed to `createContainer` ' +
      `for component \`${componentName}\`.\n` +
      `Expected an object, instead recieved \`${serialize(options)}\`.`
    )
  );

  assert(
    options.$queries !== undefined || options.$fragments !== undefined,
    (
      `Query definition for component \`${componentName}\` doesn't contain ` +
      'a `$queries` or `$fragments` definition.'
    )
  );

  assert(
    options.$renderLoading === undefined || typeof options.$renderLoading === 'function',
    (
      'Unexpected `$renderLoading` type passed to `createContainer` ' +
      `for component \`${componentName}\`.\n` +
      `Expected a function, instead recieved \`${serialize(options.$renderLoading)}\`.`
    )
  );

  assert(
    options.$renderError === undefined || typeof options.$renderError === 'function',
    (
      'Unexpected `$renderError` type passed to `createContainer` ' +
      `for component \`${componentName}\`.\n` +
      `Expected a function, instead recieved \`${serialize(options.$renderError)}\`.`
    )
  );

  assert(
    options.$renderError === undefined || options.$queries !== undefined,
    (
      `\`${componentName}\` defines a \`$renderError\` function but no \`$queries\`. ` +
      'This will never be used since error handling is only done in a container with `$queries`.'
    )
  );
}

function isStaticPath(path) {
  return typeof path === 'string';
}

function getStaticPaths(paths) {
  return Array.from(paths).filter(isStaticPath);
}

function getPathsWithDependencies(paths) {
  return Array.from(paths).filter((path) => !isStaticPath(path));
}

export function createFragment(fragmentPrefix, fragmentName, fragment) {
  return {
    [`${fragmentPrefix}:${fragmentName}`]: {
      $type: 'fragment',
      $fields: fragment,
    },
  };
}

export default function createContainer(options, transform = identity) {
  return (BaseComponent) => {
    const baseDisplayName = getDisplayName(BaseComponent);
    const connectDisplayName = `MusterContainer(${baseDisplayName})`;
    const fragmentPrefix = `${uniqueId('fragment:')}:${baseDisplayName}`;

    validateOptions(options, connectDisplayName);
    const { $queries, $fragments, $renderLoading, $renderError } = options;

    let queryPaths;
    if ($queries) {
      try {
        queryPaths = getQueryPaths($queries);
      } catch (error) {
        // Prefix any validation errors with the component name
        const errorMessage = `Error in \`${connectDisplayName}\`: ${error.message}`;
        throw new Error(errorMessage);
      }
    }

    function extractFragment(props, key) {
      const fragmentProp = props[key];
      assert(
        fragmentProp !== undefined,
        `Missing fragment prop \`${key}\` passed to component \`${connectDisplayName}\``
      );

      const fragments = fragmentProp[FRAGMENTS_SYMBOL];
      assert(
        fragments !== undefined,
        (
          `Prop \`${key}\` passed to component \`${connectDisplayName}\` \n` +
          'does not contain any fragment data. Make sure you are passing the ' +
          'correct variable to the component.'
        )
      );

      const fragment = fragmentProp[FRAGMENTS_SYMBOL][`${fragmentPrefix}:${key}`];
      assert(
        fragment !== undefined,
        (
          `Prop \`${key}\` passed to component \`${connectDisplayName}\` \n` +
          'does not contain the correct fragment data. Make sure you are passing the ' +
          'correct variable to the component.'
        )
      );

      return fragment;
    }

    function processFragments(muster, props) {
      let isReady = true;

      const fragmentState = Object.keys($fragments).reduce((memo, key) => {
        /* eslint-disable no-param-reassign */
        const fragment = $fragments[key];
        const fragmentProp = extractFragment(props, key);

        const result = createQueryState(
          muster, fragment, fragmentProp.$pathValues, fragmentProp.$path
        );
        if (result.isReady === false) {
          isReady = false;
        }

        memo[key] = result.state;
        return memo;
        /* eslint-enable no-param-reassign */
      }, {});

      return {
        isReady,
        state: fragmentState,
      };
    }

    function getQueryProps(muster, props, pathValues) {
      let isReady = true;
      let queryProps = {};

      if (!!$queries) {
        const result = createQueryState(muster, $queries, pathValues);
        if (result.isReady === false) {
          isReady = false;
        }
        queryProps = {
          ...result.state,
        };
      }

      // Process fragments
      if (!!$fragments) {
        const fragments = processFragments(muster, props);
        if (fragments.isReady === false) {
          isReady = false;
        }
        queryProps = {
          ...queryProps,
          ...fragments.state,
        };
      }

      return { isReady, queryProps };
    }

    function getMuster(props, context) {
      const muster = props.muster || context.muster;

      if (!muster) {
        throw new Error(
          `Could not find \`muster\` in either the context or props of \`${connectDisplayName}\`.`
        );
      }

      return muster;
    }

    function logRender(context, { isReady }) {
      if (!PRODUCTION && options.$debug) {
        const { pathValues } = context.state;

        /* eslint-disable no-console */
        consoleGroupCollapsed(
          `[$debug] 🎨 ${connectDisplayName}.render @ ${formatTime(new Date())}`
        );
        if (!isReady) {
          const dependendantPaths = flatMap(
            getPathsWithDependencies(queryPaths),
            ({ dependencies }) => dependencies
          );
          const allPendingPaths = pendingPaths(pathValues).concat(dependendantPaths);
          console.log('⌛️ Waiting on paths to resolve:', allPendingPaths);
        }
        console.log('`isReady`:', isReady);
        if (!!options.$queries) {
          console.log('`queryState`:', formatPathValues(pathValues));
        } else if (!!options.$fragments) {
          // Collate relevant pathValues from fragments
          const fragmentPathValues = Object.keys(options.$fragments).reduce((memo, fragKey) => {
            const fragProp = extractFragment(context.props, fragKey);
            const filteredPathValues = flow(
              filterPathValues.bind(null, (path) => path.indexOf(fragProp.$path) === 0),
              replacePath.bind(null, (path) =>
                `${fragKey}${path.substring(fragProp.$path.length)}`),
            )(fragProp.$pathValues);

            return new Map([
              ...memo,
              ...filteredPathValues,
            ]);
          }, new Map());
          console.log('`queryState`:', formatPathValues(fragmentPathValues));
        }
        consoleGroupEnd();
        /* eslint-enable no-console */
      }
    }

    /**
     * @param {Array} errors - Array of errors
     * @param {Object} pureProps - Props passed to component
     * @return {?ReactElement} - Element to render
     */
    function renderErrors(errors, pureProps) {
      // If there is a $renderError handler then use it
      if ($renderError !== undefined) {
        return $renderError(errors, pureProps);
      }

      if (!PRODUCTION) {
        let error;
        if (errors.length === 1) {
          const origError = errors[0];
          error = new Error(
            `Unhandled exception in route \`${origError.route}\`: ${origError.message}`
          );
          error.name = connectDisplayName;
          error.stack = origError.stack;
        } else {
          // create a composite error
          const errorMessage = 'Multiple errors in muster query. Open the console to see more.';
          error = new Error(errorMessage);
          error.name = connectDisplayName;
        }

        return renderError(connectDisplayName, error);
      }

      // Don't render anything if we are in production
      return null;
    }

    class ConnectedComponent extends Component {
      static displayName = connectDisplayName

      static contextTypes = {
        muster: RouterShape,
      }

      static propTypes = {
        muster: RouterShape,
      }

      static BaseComponent = BaseComponent

      static getFragment(fragmentName) {
        assert(
          $fragments[fragmentName] !== undefined,
          `Fragment \`${fragmentName}\` is not defined on \`${connectDisplayName}\``
        );

        return createFragment(fragmentPrefix, fragmentName, $fragments[fragmentName]);
      }

      constructor(props, context) {
        super(props, context);

        this.muster = getMuster(props, context);

        const expandedPaths = !!queryPaths ?
          uniq(flatMap(Array.from(queryPaths).filter(isStaticPath), (query) => expand(query))) : [];

        // Load initial values from the muster store
        const { store } = this.muster;

        // We need an indication that a value does not exist in the cache since
        // a value in the cache can legitimately be `undefined`.
        const NO_VALUE = Symbol('NO_VALUE');
        const initialValues = expandedPaths.map(
          (path) => (store.has([path]) ? store.get([path])[0] : NO_VALUE)
        );
        const initialPathValues = zip(expandedPaths, initialValues).map(([path, value]) => ({
          path,
          value: value === NO_VALUE ? PENDING : value,
        }));

        this.state = {
          pathValues: initPathValues(initialPathValues),
          errors: new Map(),
        };

        this.createSubscription = this.createSubscription.bind(this);
      }

      componentDidMount() {
        // Fetch queries
        if (!queryPaths) {
          return;
        }

        const { muster } = this;
        this.subscription = this.createSubscription(muster);
      }

      componentWillReceiveProps(nextProps, nextContext) {
        const maybeNextMuster = getMuster(nextProps, nextContext);

        if (this.subscription && maybeNextMuster !== this.muster) {
          this.subscription.unsubscribe();
          this.subscription = this.createSubscription(maybeNextMuster);
        }
      }

      componentWillUnmount() {
        if (!this.subscription) {
          return;
        }
        this.subscription.unsubscribe();
        this.subscription = null;
      }

      createSubscription(muster) {
        const pathsWithDependencies = getPathsWithDependencies(queryPaths);
        const dependendantPaths = flatMap(
          pathsWithDependencies, ({ dependencies }) => dependencies
        );
        const staticPathsObservable = Observable.of(getStaticPaths(queryPaths));
        let dependenciesObservable = Observable.of([]);
        if (dependendantPaths.length > 0) {
          dependenciesObservable = watchAll(muster.get(dependendantPaths))
            .distinctUntilChanged((a, b) => zip(a, b).every(
              ([a2, b2]) => a2.value === b2.value
            ))
            .do(handleMusterErrors.bind(this))
            .filter((pathValues) => pathValues.every(({ value }) => !isError(value)))
            .map((pathValues) => {
              addPathValues(pathValues, this.state.pathValues);
              return flatMap(pathsWithDependencies, ({ dependencies, resolve }) =>
                Array.from(resolve(dependencies.map((dependency) =>
                  find(pathValues, { path: dependency })
                )))
              );
            });
        }

        return staticPathsObservable
          .combineLatest(dependenciesObservable)
          .map((combined) => flatMap(combined.filter(Boolean)))
          .switchMap((paths) => muster.get(paths))
          .do(handleMusterErrors.bind(this))
          .map((pathValues) =>
            // Add the new path values to the values cache, and return it
            addPathValues(pathValues, this.state.pathValues)
          )
          .debounceTime(0)
          .subscribe((pathValues) => {
            this.setState({ pathValues });
          }, (error) => {
            throw error;
          });

        function handleMusterErrors(pathValues) {
          const errors = pathValues
            .filter(({ value }) => isError(value))
            .map(({ value }) =>
              deserializeMusterError(value.value)
            );

          // Log out all errors to the console
          // N.B. this needs to be async since in ReactNative we throw the composite
          // error first so that that is the one that gets shown to the developer.
          setTimeout(() => {
            errors.forEach((err) => {
              console.error( // eslint-disable-line no-console
                `Unhandled exception in route \`${err.route}\` -`,
                err.stack
              );
            });
          }, 5);

          if (errors.length > 0) {
            this.setState((prevState) => ({
              errors: errors.reduce(
                (acc, err) => acc.set(err.path, err),
                prevState.errors,
              ),
            }));
          }
        }
      }

      render() {
        const { muster } = this;
        const { pathValues, errors } = this.state;

        // remove fragment props
        const pureProps = omit(this.props, $fragments ? Object.keys($fragments) : []);

        // if there are any errors then bail out
        if (errors.size > 0) {
          return renderErrors([...errors.values()], pureProps);
        }

        const { isReady, queryProps } = getQueryProps(muster, this.props, pathValues);

        logRender(this, { options, isReady, connectDisplayName });

        // If queries aren't ready then don't render anything
        if (!isReady) {
          if ($renderLoading === undefined) {
            return null;
          }

          return $renderLoading(pureProps);
        }

        const injectedProps = transform(queryProps, pureProps);

        return createElement(BaseComponent, {
          ...pureProps,
          ...injectedProps,
        });
      }
    }

    return hoistStatics(ConnectedComponent, BaseComponent);
  };
}
